import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import { API_BASE } from "@/lib/api";

const MAX_POLLS = 8;
const POLL_INTERVAL = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, paid, expired, timeout, error
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        const res = await fetch(`${API_BASE}/checkout/status/${sessionId}`);
        if (!res.ok) throw new Error("failed");
        const data = await res.json();
        setDetails(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        if (attempts >= MAX_POLLS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const message = {
    checking: { icon: Loader2, spin: true, title: "Confirming your payment…", body: "This usually takes a few seconds." },
    paid: { icon: CheckCircle2, title: "You're all set.", body: "Welcome to NextVisit. We've sent your receipt and next steps to your inbox." },
    expired: { icon: XCircle, title: "Session expired", body: "The checkout window timed out. Head back to pricing to try again." },
    timeout: { icon: Loader2, title: "Still processing", body: "Payment is still confirming with Stripe. Check your email in a minute." },
    error: { icon: XCircle, title: "Something went wrong", body: "We couldn't confirm the payment. Contact us if you were charged." },
  }[status];

  const Icon = message.icon;

  return (
    <div className="min-h-screen bg-[#FAF7F0] flex flex-col">
      <header className="border-b border-[#E5E0D8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center">
          <Link to="/" data-testid="payment-nav-logo" className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-2xl bg-[#183D36] text-[#F4B183] flex items-center justify-center">
              <Sparkles size={16} />
            </span>
            <span className="font-heading text-lg font-semibold text-[#183D36]">NextVisit</span>
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="max-w-lg text-center" data-testid={`payment-status-${status}`}>
          <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-6 ${
            status === "paid" ? "bg-[#A8BFA3]" :
            status === "expired" || status === "error" ? "bg-[#F4B183]" : "bg-[#EFECE5]"
          }`}>
            <Icon size={32} className={`text-[#183D36] ${message.spin ? "animate-spin" : ""}`} />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#183D36] mb-3">{message.title}</h1>
          <p className="text-[#4A4A4A] text-lg mb-8">{message.body}</p>

          {details && status === "paid" && (
            <div className="mb-8 inline-block rounded-2xl bg-white border border-[#E5E0D8] p-6 text-left">
              <div className="overline mb-2">Receipt</div>
              <div className="text-sm text-[#4A4A4A]">Amount</div>
              <div className="font-heading text-2xl text-[#183D36]">
                ${(details.amount_total / 100).toFixed(2)} {details.currency?.toUpperCase()}
              </div>
              {details.metadata?.package_id && (
                <div className="mt-3 text-sm text-[#4A4A4A]">Plan: <span className="text-[#183D36] font-medium capitalize">{details.metadata.package_id}</span></div>
              )}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild data-testid="payment-home-btn" className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] px-8 py-6">
              <Link to="/">Back home</Link>
            </Button>
            {status !== "paid" && (
              <Button asChild variant="ghost" className="rounded-full border border-[#183D36]/20 px-8 py-6">
                <Link to="/#pricing">See pricing</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
